import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useContent } from "../content/ContentProvider";
import { RouteFadeContext } from "../App";
import FilmBackdrop from "../components/FilmBackdrop";
import { ArrowLeft } from "lucide-react";
import { layoutWorksGallery, pageHeightVh } from "../lib/worksLayout";

/**
 * Works gallery: 8 landscape cards laid out down a tall scrollable page,
 * with only slight corner overlaps. Each card stays hidden until the cursor
 * approaches it, then snaps to fully opaque (no glassy partial transparency).
 * Clicking a card opens that project's detail page.
 */
export default function WorksGallery() {
  const { fadeTo } = useContext(RouteFadeContext);
  const { works: ALL_WORKS, worksDeeper } = useContent();
  const { WORKS, POSITIONS } = useMemo(() => {
    const works = ALL_WORKS.filter((w) => w.enabled !== false);
    return {
      WORKS: works,
      POSITIONS: layoutWorksGallery(works.length),
    };
  }, [ALL_WORKS]);

  // Use the auto-layout height as the base; extend only if a saved tile position
  // sits beyond it. 15vh is a safe upper bound for gallery tile height at any
  // reasonable viewport (landscape images at 31vw are typically 10–20vh tall).
  const PAGE_HEIGHT = useMemo(() => {
    const autoHeight = pageHeightVh(WORKS.length);
    const savedMaxY = WORKS.reduce((max, w) => (
      typeof w.y === "number" ? Math.max(max, w.y) : max
    ), 0);
    return Math.max(autoHeight, savedMaxY + 15);
  }, [WORKS]);
  const WORKS_DEEPER_BACKGROUND = worksDeeper.background;
  const galleryBottomMargin = worksDeeper.galleryBottomMargin ?? 0.5;
  const [innerHeight, setInnerHeight] = useState(() => window.innerHeight);
  useEffect(() => {
    const onResize = () => setInnerHeight(window.innerHeight);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const marginVh = (galleryBottomMargin * 96) / (innerHeight / 100);
  const cardRefs = useRef([]);
  const currentOpacities = useRef([]);
  const cursorRef = useRef({ x: -9999, y: -9999 });
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    document.title = "Selected Works · California Sexy";
  }, []);

  // Resync refs with WORKS.length whenever the visible count changes — e.g.
  // when admin toggles `enabled` on a work and the saved doc propagates back
  // through useContent without a page navigation. Without this, stale entries
  // in currentOpacities/cardRefs would leak through the raf loop.
  useEffect(() => {
    currentOpacities.current = Array.from({ length: WORKS.length }, (_, i) => (
      typeof currentOpacities.current[i] === "number" ? currentOpacities.current[i] : 0
    ));
    cardRefs.current = cardRefs.current.slice(0, WORKS.length);
  }, [WORKS.length]);

  // Sparse layouts (≤ 4 visible works) need a baseline opacity floor so
  // every card stays discoverable without the user having to hunt with the
  // cursor. The original cursor-proximity-only reveal was tuned for the
  // dense 8-card grid where SOMETHING was always near the cursor; with one
  // or two cards in the upper-left it could read as a blank page. Above
  // the threshold the original behavior is preserved.
  const baseOpacity = WORKS.length <= 4 ? 0.55 : 0;

  useEffect(() => {
    const onMove = (e) => {
      cursorRef.current = { x: e.clientX, y: e.clientY };
    };
    window.addEventListener("mousemove", onMove);

    let raf;
    const INNER_RADIUS = 260;
    const OUTER_RADIUS = 380;
    const tick = () => {
      const cx = cursorRef.current.x;
      const cy = cursorRef.current.y;
      let nearest = null;
      let nearestDist = Infinity;

      for (let i = 0; i < cardRefs.current.length; i++) {
        const el = cardRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        const ex = rect.left + rect.width / 2;
        const ey = rect.top + rect.height / 2;
        const dx = ex - cx;
        const dy = ey - cy;
        const dist = Math.sqrt(dx * dx + dy * dy);
        let proximity;
        if (dist <= INNER_RADIUS) proximity = 1;
        else if (dist >= OUTER_RADIUS) proximity = 0;
        else proximity = (OUTER_RADIUS - dist) / (OUTER_RADIUS - INNER_RADIUS);
        const target = baseOpacity + (1 - baseOpacity) * proximity;

        if (dist < nearestDist) {
          nearestDist = dist;
          nearest = i;
        }

        const cur = currentOpacities.current[i];
        const speed = target > cur ? 0.14 : 0.06;
        const next = cur + (target - cur) * speed;
        currentOpacities.current[i] = next;
        el.style.opacity = next.toFixed(3);
        el.style.zIndex = next > 0.05 ? Math.round(10 + next * 20) : 1;
      }

      const showLabel =
        nearest !== null && nearestDist < OUTER_RADIUS ? nearest : null;
      setHovered((prev) => (prev !== showLabel ? showLabel : prev));

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", onMove);
      cancelAnimationFrame(raf);
    };
  }, [baseOpacity]);

  const goBack = (e) => {
    e.preventDefault();
    fadeTo("/works", 600);
  };

  const goWork = (e, slug) => {
    e.preventDefault();
    fadeTo(`/works/deeper/${slug}`, 700);
  };

  return (
    <div
      className="page-enter"
      style={{
        position: "relative",
        minHeight: `${PAGE_HEIGHT + marginVh}vh`,
        width: "100vw",
        backgroundColor: "#000",
        backgroundImage: `url("${WORKS_DEEPER_BACKGROUND}")`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center top",
        backgroundSize: "cover",
        backgroundAttachment: "fixed",
        overflowX: "hidden",
      }}
    >
      {/* Shared atmospheric backdrop — fixed behind everything. Gated by worksDeeper.filmBackdrop (default false). */}
      {worksDeeper.filmBackdrop && <FilmBackdrop />}

      {/* Back link — fixed so it stays visible while scrolling */}
      <a
        href="/works"
        onClick={goBack}
        className="font-math"
        style={{
          position: "fixed",
          top: "1.4rem",
          left: "1.6rem",
          color: "#f4ece0",
          textDecoration: "none",
          fontSize: "0.95rem",
          display: "inline-flex",
          alignItems: "center",
          gap: "0.5rem",
          opacity: 0.85,
          zIndex: 60,
        }}
      >
        <ArrowLeft size={16} strokeWidth={1.5} />
        back
      </a>

      {/* Image cards — laid out vertically, slight corner overlap */}
      {WORKS.map((w, i) => {
        const auto = POSITIONS[i] || { x: 6, y: 6, w: 31 };
        // Per-field override — drags from LayoutModal can set x/y without w
        // (and vice versa). Match WorkDetail.jsx's gallery fallback pattern.
        const pos = {
          x: typeof w.x === "number" ? w.x : auto.x,
          y: typeof w.y === "number" ? w.y : auto.y,
          w: typeof w.w === "number" ? w.w : auto.w,
        };
        return (
          <Link
            key={w.id}
            to={`/works/deeper/${w.slug}`}
            onClick={(e) => goWork(e, w.slug)}
            ref={(el) => (cardRefs.current[i] = el)}
            aria-label={`Open ${w.title}`}
            style={{
              position: "absolute",
              left: `${pos.x}%`,
              top: `${pos.y}vh`,
              width: `${pos.w}vw`,
              opacity: 0,
              display: "block",
              cursor: "pointer",
              overflow: "hidden",
              textDecoration: "none",
              transition: "transform 500ms ease",
              zIndex: 5,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.01)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
            }}
          >
            <img
              src={w.image}
              alt={w.title}
              loading="lazy"
              decoding="async"
              style={{
                width: "100%",
                height: "auto",
                display: "block",
                filter: "contrast(1.05) saturate(0.92) brightness(0.95)",
                pointerEvents: "none",
              }}
            />
          </Link>
        );
      })}

      {/* Hovered title — fixed at bottom of viewport */}
      <div
        className="font-math"
        style={{
          position: "fixed",
          bottom: "2rem",
          left: "50%",
          transform: "translateX(-50%)",
          color: "#f4ece0",
          fontSize: "0.95rem",
          letterSpacing: "0.06em",
          opacity: hovered !== null ? 0.92 : 0,
          transition: "opacity 600ms ease",
          textAlign: "center",
          zIndex: 65,
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {hovered !== null ? (
          <>
            <em style={{ fontStyle: "italic" }}>{WORKS[hovered].title}</em>
            <span style={{ opacity: 0.6 }}>
              {"  \u2014  "}
              {WORKS[hovered].year}
            </span>
          </>
        ) : (
          "\u00a0"
        )}
      </div>
    </div>
  );
}
